// @ts-strict-ignore
import React, {
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react';
import {
  AdvancedMenuContent,
  Icon,
  IconName,
} from '@usealloy/component-library';
import type { AdvancedMenuItemType } from '@usealloy/component-library';
import { AggregationEditorContext } from './context';
import { buildOptionGroups, getColumnKey, toFilterOptions } from './helpers';
import type { AggregationFilter } from 'flow/store/engine/types';
import type { AggregationFilterOption } from '~/dashboard/views/settings/aggregations/types';
import { AggregationFilterOperatorType } from 'flow/store/engine/constants';
import { StyledFilterTriggerButton, StyledFilterDropdownMenu } from './styles';
import AggregationFilterRow from './AggregationFilterRow';

const AggregationFiltersSection = () => {
  const {
    sourceData, setSourceData, aggregationConfig,
  } = useContext(AggregationEditorContext);

  const [menuIsOpen, setMenuIsOpen] = useState(false);

  const filterOptions = useMemo(
    () => toFilterOptions(aggregationConfig.columns),
    [aggregationConfig.columns],
  );
  const filterOptionsGrouped = useMemo(
    () => buildOptionGroups(filterOptions, aggregationConfig.objectDisplayName),
    [filterOptions, aggregationConfig.objectDisplayName],
  );

  const handleFilterToggle = useCallback((option: AggregationFilterOption, checked: boolean) => {
    if (checked) {
      const newFilter: AggregationFilter = {
        columnSlug: option.column_slug ?? option.value,
        operatorSlug: AggregationFilterOperatorType.EQ,
        staticValue: '',
        dynamicColumnSlug: null,
        ...(option.ui_data_type === 'published_attribute' && {
          publishedAttributeMetadata: {
            publishedAttributeToken: option.value,
            publishedAttributeDescription: option.published_attribute_description ?? null,
            publishedAttributeName: option.published_attribute_name,
            publishedAttributeDataType: option.data_type,
            containsPii: option.contains_pii,
          },
        }),
      };
      setSourceData(prev => ({
        ...prev,
        filters: [...prev.filters, newFilter],
      }));
    } else {
      setSourceData(prev => ({
        ...prev,
        filters: prev.filters.filter(f => getColumnKey(f) !== option.value),
      }));
    }
  }, [setSourceData]);

  const handleUpdateFilter = useCallback((
    filterKey: string,
    updates: Partial<AggregationFilter>,
  ) => {
    setSourceData(prev => ({
      ...prev,
      filters: prev.filters.map(f => (
        getColumnKey(f) === filterKey ? { ...f, ...updates } : f
      )),
    }));
  }, [setSourceData]);

  const handleRemoveFilter = useCallback((filterKey: string) => {
    setSourceData(prev => ({
      ...prev,
      filters: prev.filters.filter(f => getColumnKey(f) !== filterKey),
    }));
  }, [setSourceData]);

  const menuItems: AdvancedMenuItemType[] = useMemo(
    () => filterOptionsGrouped.map(group => ({
      type: 'group' as const,
      label: group.label,
      items: group.options.map(option => {
        const isActive = sourceData.filters.some(
          f => getColumnKey(f) === option.value,
        );
        return {
          value: option.value,
          label: option.label,
          showCheckbox: true,
          checked: isActive,
          closeOnSelect: false,
          onCheckboxChange: (checked: boolean) => handleFilterToggle(option, checked),
          onSelect: () => handleFilterToggle(option, !isActive),
        };
      }),
    })),
    [filterOptionsGrouped, sourceData.filters, handleFilterToggle],
  );
  if (filterOptions.length === 0) return null;

  return (
    <>
      {sourceData.filters.map(filter => {
        const key = getColumnKey(filter);
        const option = filterOptions.find(o => o.value === key);
        return (
          <AggregationFilterRow
            key={key}
            filter={filter}
            filterOption={option}
            onUpdate={handleUpdateFilter}
            onRemove={handleRemoveFilter}
          />
        );
      })}

      <StyledFilterDropdownMenu
        isOpen={menuIsOpen}
        onOpenChange={setMenuIsOpen}
        align="start"
        trigger={(
          <StyledFilterTriggerButton>
            Add/Remove Filter
            <Icon icon={menuIsOpen ? IconName.CaretUp : IconName.CaretDown} size="small" />
          </StyledFilterTriggerButton>
        )}
      >
        <AdvancedMenuContent
          items={menuItems}
          enableMainFilter
          mainFilterPlaceholder="Search"
          emptyMessage="No filters available."
          dynamicMenuHeight
          onClose={() => setMenuIsOpen(false)}
        />
      </StyledFilterDropdownMenu>
    </>
  );
};

export default AggregationFiltersSection;
